import {apiClient} from "../../shared/api/client";
import {requireActorCountryScope} from "../../shared/finance/actorCountryScope";
import {addMoney, compareMoney, normalizeMoney} from "../../shared/finance/decimal";
import {
    normalizeSplitBill,
    normalizeSplitBillList,
    SPLIT_GROUP_STATUSES,
    SplitBillContractError,
} from "./contracts";

const SPLIT_BILLS_PATH = "/api/v2/admin/pay/split-bills";
const PAY_SEARCH_PATH = "/api/v2/admin/pay/search-names";
const STAFF_BRANCH_PATH = "/api/v2/admin/staff/branches";
export const MAX_SPLIT_BILL_AMOUNT = "999999999.99";

export const splitBillKeys = {
    all: (scope) => ["split-bills", String(scope)],
    list: (scope, filters) => ["split-bills", String(scope), "list", filters],
    detail: (scope, groupId) => ["split-bills", String(scope), "detail", String(groupId)],
    status: (scope, groupId) => ["split-bills", String(scope), "status", String(groupId)],
};

const tenantScope = (countryScope, actorCountryId) =>
    requireActorCountryScope({countryScope, actorCountryId});

const positiveInteger = (value, label) => {
    const text = String(value ?? "").trim();
    if (!/^[1-9]\d*$/.test(text)) throw new Error(`${label} должен быть положительным числом`);
    const parsed = Number(text);
    if (!Number.isSafeInteger(parsed)) {
        throw new Error(`${label} должен быть безопасным целым числом`);
    }
    return parsed;
};

const optionalPositiveInteger = (value, label) =>
    value === "" || value === null || value === undefined
        ? undefined
        : positiveInteger(value, label);

const groupId = (value) => {
    const result = String(value ?? "").trim();
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(result)) {
        throw new SplitBillContractError("group_id must be a UUID");
    }
    return result;
};

const limitValue = (value) => Math.min(100, positiveInteger(value ?? 50, "Лимит"));

const statusValue = (value) => {
    if (value === "" || value === null || value === undefined) return undefined;
    if (!SPLIT_GROUP_STATUSES.includes(value)) throw new Error("Некорректный статус split bill");
    return value;
};

const namedIds = (value, label) => {
    if (!Array.isArray(value)) throw new SplitBillContractError(`${label} must be an array`);
    return value.map((item) => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
            throw new SplitBillContractError(`${label} item must be an object`);
        }
        const id = positiveInteger(item.id, `${label} id`);
        if (typeof item.name !== "string" || !item.name.trim()) {
            throw new SplitBillContractError(`${label} name must be text`);
        }
        return {id, name: item.name};
    });
};

export const listSplitBills = async ({countryScope, actorCountryId, status = "", limit = 50}) => {
    tenantScope(countryScope, actorCountryId);
    const normalizedStatus = statusValue(status);
    const response = await apiClient.get(SPLIT_BILLS_PATH, {
        params: {
            ...(normalizedStatus ? {status: normalizedStatus} : {}),
            limit: limitValue(limit),
        },
    });
    return normalizeSplitBillList(response.data);
};

export const createSplitBill = async ({
    countryScope,
    actorCountryId,
    amount,
    partsCount,
    branchId,
    personalId,
    expiresInMinutes,
    idempotencyKey,
}) => {
    tenantScope(countryScope, actorCountryId);
    const normalizedAmount = normalizeMoney(amount);
    if (compareMoney(normalizedAmount, "0.00") <= 0) throw new Error("Сумма должна быть больше нуля");
    if (compareMoney(normalizedAmount, MAX_SPLIT_BILL_AMOUNT) > 0) {
        throw new Error(`Сумма не должна превышать ${MAX_SPLIT_BILL_AMOUNT}`);
    }
    const normalizedIdempotencyKey = String(idempotencyKey ?? "").trim();
    if (normalizedIdempotencyKey.length < 8 || normalizedIdempotencyKey.length > 128) {
        throw new Error("Ключ идемпотентности должен содержать от 8 до 128 символов");
    }
    const normalizedPartsCount = positiveInteger(partsCount, "Количество частей");
    if (normalizedPartsCount < 2 || normalizedPartsCount > 20) {
        throw new Error("Количество частей должно быть от 2 до 20");
    }
    const minimumAmount = addMoney(Array.from({length: normalizedPartsCount}, () => "0.01"));
    if (compareMoney(normalizedAmount, minimumAmount) < 0) {
        throw new Error("Сумма каждой части должна быть не меньше 0.01");
    }
    const normalizedBranchId = optionalPositiveInteger(branchId, "Филиал");
    const normalizedPersonalId = optionalPositiveInteger(personalId, "Сотрудник");
    const normalizedExpiry = optionalPositiveInteger(expiresInMinutes, "Срок оплаты");
    if (normalizedExpiry !== undefined && (normalizedExpiry < 1 || normalizedExpiry > 1440)) {
        throw new Error("Срок оплаты должен быть от 1 до 1440 минут");
    }
    const response = await apiClient.post(SPLIT_BILLS_PATH, {
        amount: normalizedAmount,
        parts_count: normalizedPartsCount,
        ...(normalizedBranchId ? {branch_id: normalizedBranchId} : {}),
        ...(normalizedPersonalId ? {personal_id: normalizedPersonalId} : {}),
        ...(normalizedExpiry ? {expires_in_minutes: normalizedExpiry} : {}),
        idempotency_key: normalizedIdempotencyKey,
    });
    return normalizeSplitBill(response.data);
};

export const getSplitBill = async ({countryScope, actorCountryId, groupId: value}) => {
    tenantScope(countryScope, actorCountryId);
    const response = await apiClient.get(`${SPLIT_BILLS_PATH}/${groupId(value)}`);
    return normalizeSplitBill(response.data);
};

export const getSplitBillStatus = async ({countryScope, actorCountryId, groupId: value}) => {
    tenantScope(countryScope, actorCountryId);
    const response = await apiClient.get(`${SPLIT_BILLS_PATH}/${groupId(value)}/status`);
    return normalizeSplitBill(response.data);
};

export const cancelSplitBill = async ({countryScope, actorCountryId, groupId: value}) => {
    tenantScope(countryScope, actorCountryId);
    const response = await apiClient.post(`${SPLIT_BILLS_PATH}/${groupId(value)}/cancel`);
    return normalizeSplitBill(response.data);
};

export const searchSplitBillBranches = async ({countryScope, actorCountryId, query}) => {
    const scope = tenantScope(countryScope, actorCountryId);
    const branchName = String(query ?? "").trim();
    if (branchName.length < 2) throw new Error("Введите минимум два символа названия филиала");
    const response = await apiClient.post(
        PAY_SEARCH_PATH,
        {place_name: "", branch_name: branchName},
        {params: {country_id: scope}},
    );
    return namedIds(response.data, "branches");
};

export const listSplitBillStaff = async ({countryScope, actorCountryId, branchId}) => {
    tenantScope(countryScope, actorCountryId);
    const id = positiveInteger(branchId, "Филиал");
    const response = await apiClient.get(`${STAFF_BRANCH_PATH}/${id}`);
    const payload = response.data;
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        throw new SplitBillContractError("staff response must be an object");
    }
    if (positiveInteger(payload.branch_id, "staff branch_id") !== id) {
        throw new SplitBillContractError("staff branch_id must match requested branch");
    }
    const staff = namedIds(payload.staff, "staff");
    return staff.map((item, index) => {
        const phoneNumber = payload.staff[index]?.phone_number;
        if (typeof phoneNumber !== "string") {
            throw new SplitBillContractError("staff phone_number must be text");
        }
        return {...item, phone_number: phoneNumber};
    });
};
